import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { classificationApi } from '../api';
import { snakeToCamel } from '../utils/apiTransform';

// Single polling cadence — auto-adjusts based on cached state below.
// Idle (no active scan): every 30s — low-cost heartbeat.
// Running (active scan):  every 2s — keeps the progress bar fresh.
const SLOW_POLL_MS = 30_000;
const FAST_POLL_MS = 2_000;

const ACTIVE_SCAN_KEY = ['classification', 'scan', 'active'];

async function fetchActiveScan() {
  const result = await classificationApi.getActiveScan();
  const scan = result.data?.data?.scan ?? null;
  return scan ? snakeToCamel(scan) : null;
}

/**
 * App-wide source of truth for "is a classification scan running right now?".
 * MUST be mounted exactly once — at the app shell level (AppLayout) — to
 * avoid duplicate polling streams and duplicate terminal-status toasts.
 * Other components read the cached value via `useActiveClassificationScanReadOnly`.
 *
 * Polling cadence is automatic: 5s when idle, 2s while a scan is in progress.
 * Polling continues regardless of which route the user is on, so navigating
 * away from /classification does not cause the UI to forget the running scan.
 */
export default function useActiveClassificationScan() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('classification');
  const lastTerminalIdRef = useRef(null);
  const lastNonTerminalIdRef = useRef(null);
  // Stable ref so the terminal-status useEffect never re-fires just because
  // i18next swapped a memoised translation function (it does this when a
  // namespace lazy-loads — which the call to useTranslation above can
  // trigger). Without this, the effect would loop, fire 7+ invalidations,
  // and lock up the UI on tab navigation.
  const tRef = useRef(t);
  tRef.current = t;

  const { data: activeScan = null, refetch } = useQuery({
    queryKey: ACTIVE_SCAN_KEY,
    queryFn: fetchActiveScan,
    // Auto-adjust: poll fast while a scan is running, slow when idle.
    // Using the function form so the interval reacts immediately when the
    // cached value flips between null and a scan dict.
    refetchInterval: (query) => (query.state.data ? FAST_POLL_MS : SLOW_POLL_MS),
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  const activeScanId = activeScan?.id || null;

  // Detect terminal transitions: when the previously-active scan id
  // disappears from /scans/active, fetch its final status to decide which
  // toast to surface. Effect deps are PRIMITIVES only so the effect cannot
  // be re-fired by reference churn from useQuery / useTranslation.
  useEffect(() => {
    if (activeScanId) {
      lastNonTerminalIdRef.current = activeScanId;
      return undefined;
    }
    const finishedId = lastNonTerminalIdRef.current;
    if (!finishedId || lastTerminalIdRef.current === finishedId) return undefined;
    lastTerminalIdRef.current = finishedId;
    lastNonTerminalIdRef.current = null;

    let cancelled = false;
    classificationApi
      .getScanRunStatus(finishedId)
      .then((result) => {
        if (cancelled) return;
        const scan = snakeToCamel(result.data?.data || result.data || {});
        const tNow = tRef.current;
        const status = scan?.status;
        if (status === 'completed') {
          toast.success(tNow('toast.scanCompleted', 'Classification scan completed'));
        } else if (status === 'failed') {
          toast.error(scan?.errorMessage || tNow('toast.scanFailed', 'Classification scan failed'));
        } else if (status === 'cancelled') {
          toast.info(tNow('toast.scanCancelled', 'Classification scan cancelled'));
        }
        // Refresh consumers against the freshly-finished scan's outputs.
        queryClient.invalidateQueries({ queryKey: ['classifications'] });
        queryClient.invalidateQueries({ queryKey: ['classificationDashboard'] });
        queryClient.invalidateQueries({ queryKey: ['classificationCommandCenter'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
        queryClient.invalidateQueries({ queryKey: ['reviewCenter'] });
        queryClient.invalidateQueries({ queryKey: ['triageSummary'] });
      })
      .catch(() => {
        // Best effort — don't surface a toast for the failure of the
        // success-toast lookup itself.
      });

    return () => {
      cancelled = true;
    };
  }, [activeScanId, queryClient]);

  return {
    activeScan,
    isActive: !!activeScan,
    refetch,
  };
}

/**
 * Read-only consumer. Returns whatever the AppLayout-mounted polling hook
 * has placed in the cache. Does NOT start its own poll — there must be
 * exactly one polling stream per app for correctness (see hook above).
 */
export function useActiveClassificationScanReadOnly() {
  const { data: activeScan = null } = useQuery({
    queryKey: ACTIVE_SCAN_KEY,
    queryFn: fetchActiveScan,
    enabled: false,
    staleTime: Infinity,
  });
  return { activeScan, isActive: !!activeScan };
}

export { ACTIVE_SCAN_KEY };
